import React from 'react';
import { Box, Typography, Button, Stack, Paper, Chip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import { useAuth } from '../../shared/contexts/UserContext';
import { useOrgAccess } from '../../shared/contexts';
import { useAdminScreen } from '../admin/useAdminScreen';
import {
  CoreOrgLoadErrorState, isCoreOrgLoadBlocked, StatCard, WelcomeChecklist,
} from '../admin/AdminScreen';
import { statusChipSx, consoleCardSx, consoleCardHoverSx } from '../admin/consoleChips';

// Resource shortcut card (the reference "Resources" row): icon + title +
// one-line description; the whole card is the link.
function ResourceCard({ icon: Icon, title, desc, href, external }) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      component="a"
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      sx={{
        ...consoleCardSx(theme),
        ...consoleCardHoverSx(theme),
        flex: '1 1 220px',
        minWidth: 200,
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
          {title}
        </Typography>
        {external && <OpenInNewIcon sx={{ fontSize: 13, color: 'text.disabled' }} />}
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{desc}</Typography>
    </Paper>
  );
}

// Home — the console landing (reference: the Anthropic dashboard). A greeting,
// a row of role-shaped stat cards that deep-link into their sections, and a
// Resources row. Role shapes:
//   - Org / Billing Admin: seats, pool balance, burn (role-gated endpoints).
//   - Org member: numberless (adoption guard) — seat status only.
//   - Non-org (free / trial): their own credit balances + subscribe path.
export default function HomeSection({ goSection, usageSummary, activePlanKey, activeSeats, pod }) {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const orgAccess = useOrgAccess();
  // Role-gated org data: for non-admins this hook fetches nothing and just
  // reports canAccessAdmin === false, so it is safe to call for everyone.
  const s = useAdminScreen();

  // First-run checklist (post-checkout ?welcome=1, or a fresh solo-admin org).
  // Lives on the Dashboard now — the landing — and points actions at the flat
  // Members route. Dismissed once, remembered in localStorage.
  const [welcomeDismissed, setWelcomeDismissed] = React.useState(() => {
    try { return localStorage.getItem('restack_welcome_done') === '1'; } catch (e) { return false; }
  });
  const welcomeParam = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('welcome') === '1';
  const firstRunSoloAdmin = orgAccess.isOrgAdmin && s.org && (s.org.memberCount || 0) <= 1;
  const showWelcome = !welcomeDismissed && (welcomeParam || firstRunSoloAdmin);
  const dismissWelcome = () => {
    setWelcomeDismissed(true);
    try { localStorage.setItem('restack_welcome_done', '1'); } catch (e) { /* private mode */ }
    if (typeof window !== 'undefined') window.history.replaceState({}, '', window.location.pathname);
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = (currentUser?.displayName || '').trim().split(/\s+/)[0]
    || (currentUser?.email || '').split('@')[0]
    || '';

  const isOrgUser = !!orgAccess.orgId;
  const isAdminView = isOrgUser && s.canAccessAdmin;
  const coreOrgLoadBlocked = isAdminView && isCoreOrgLoadBlocked(s);
  const num = (n) => Number(n || 0).toLocaleString();

  // Admin stat values — '—' while the role-gated fetch is in flight.
  const seatsUsed = s.org ? `${s.org.memberCount} / ${s.org.purchasedSeats}` : '—';
  const seatsLeft = s.org
    ? Math.max(0, s.org.purchasedSeats - s.org.memberCount - (s.pendingInvites?.length || 0))
    : null;
  const pool = s.creditPool;
  const poolExhausted = pool?.poolState === 'exhausted';

  const isFree = activePlanKey === 'free';

  return (
    <Box>
      {/* Greeting header with the section's primary actions on the right. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, letterSpacing: '-0.02em' }}>
          {greeting}{firstName ? `, ${firstName}` : ''}
        </Typography>
        <Stack direction="row" spacing={1}>
          {isAdminView && (
            <>
              <Button
                variant="outlined" size="small" disableElevation
                startIcon={<BarChartRoundedIcon />}
                onClick={() => goSection('credits')}
              >
                View usage
              </Button>
              {orgAccess.isOrgAdmin && (
                <Button
                  variant="contained" size="small" disableElevation
                  startIcon={<PersonAddAltRoundedIcon />}
                  onClick={() => goSection('members')}
                >
                  Invite member
                </Button>
              )}
            </>
          )}
          {!isOrgUser && isFree && (
            <Button
              variant="contained" size="small" disableElevation
              startIcon={<BoltRoundedIcon />}
              onClick={() => goSection('plan')}
            >
              Subscribe
            </Button>
          )}
          {!isOrgUser && !isFree && (
            <Button
              variant="outlined" size="small" disableElevation
              startIcon={<ReceiptLongRoundedIcon />}
              onClick={() => goSection('plan')}
            >
              Manage plan
            </Button>
          )}
        </Stack>
      </Box>

      {showWelcome && (
        <Box sx={{ mb: 3 }}>
          <WelcomeChecklist s={{ setTab: () => goSection('members') }} onDismiss={dismissWelcome} />
        </Box>
      )}

      {coreOrgLoadBlocked && (
        <CoreOrgLoadErrorState s={s} sx={{ mb: 4 }} />
      )}

      {/* Stat cards — each deep-links into its section. */}
      {isAdminView && !coreOrgLoadBlocked && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 4 }}>
          <StatCard
            label="Seats used"
            value={seatsUsed}
            sub={seatsLeft != null ? `${seatsLeft} available` : undefined}
            icon={GroupRoundedIcon}
            onClick={() => goSection('members')}
          />
          <StatCard
            label="Credits left"
            value={pool ? num(pool.balance) : '—'}
            sub={poolExhausted ? 'Used up — top up to keep working' : 'available now'}
            accent={poolExhausted ? theme.palette.warning.main : undefined}
            icon={BoltRoundedIcon}
            onClick={() => goSection('credits')}
          />
          <StatCard
            label="Used this month"
            value={pool ? num(pool.burnThisCycle) : '—'}
            sub="credits used"
            icon={LocalFireDepartmentRoundedIcon}
            onClick={() => goSection('credits')}
          />
        </Stack>
      )}

      {/* Org member view: numberless by design (adoption guard). The calm,
          reassuring shape of a usage card with ZERO figures; the only signal
          is poolState (ok | exhausted), mapped to a status chip. No balance,
          no meter, no cost. (The numeric capacity meter lives only on the
          admin Credits surface.) */}
      {/* Not clickable: a member has no org section to land on (the admin
          routes are hidden for them), so the old deep-link was a dead door. */}
      {isOrgUser && !isAdminView && (
        <Paper
          elevation={0}
          sx={{ ...consoleCardSx(theme), mb: 4 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
            <WorkspacePremiumRoundedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
              Restack is managed by your organization
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label="Member"
              sx={statusChipSx(theme, 'neutral')}
            />
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Your organization's admins manage plan status, seats, and credits. Open an Office add-in to see whether work is currently available.
          </Typography>
        </Paper>
      )}

      {/* New (free) user: lead with the one action that matters — subscribe.
          A row of zero-value credit cards is noise before they have a plan. */}
      {!isOrgUser && isFree && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3.5 },
            mb: 4,
            borderRadius: '12px',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ maxWidth: 540 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Subscribe to get started
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Generate and edit polished slides right inside PowerPoint. Pick your seats and
                your team shares one pooled credit balance.
              </Typography>
            </Box>
            <Button
              variant="contained"
              disableElevation
              startIcon={<BoltRoundedIcon />}
              onClick={() => goSection('plan')}
              sx={{ flexShrink: 0, borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
            >
              Choose seats &amp; subscribe
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Paid (non-org) user: their real balances. */}
      {!isOrgUser && !isFree && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 4 }}>
          <StatCard
            label="Monthly credits"
            value={num(Math.floor(usageSummary?.subscriptionCreditsBalance || 0))}
            sub={usageSummary?.baseCredits > 0 ? `of ${num(usageSummary.baseCredits)} this cycle` : 'this cycle'}
            icon={BoltRoundedIcon}
            onClick={() => goSection('plan')}
          />
          <StatCard
            label="Add-on credits"
            value={num(Math.floor(usageSummary?.prepaidCreditsBalance || 0))}
            sub="in reserve"
            icon={SavingsRoundedIcon}
            onClick={() => goSection('plan')}
          />
          <StatCard
            label="Plan"
            value={`Pro · ${activeSeats} seat${activeSeats === 1 ? '' : 's'}`}
            sub="billed monthly"
            icon={ReceiptLongRoundedIcon}
            onClick={() => goSection('plan')}
          />
        </Stack>
      )}

      {/* Resources — the reference bottom row: quiet shortcut cards. */}
      <Typography sx={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em', mb: 1.5 }}>
        Resources
      </Typography>
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
        <ResourceCard
          icon={ExtensionRoundedIcon}
          title="Add-in manifest"
          desc="Deploy Restack to your firm via M365 Admin Center → Integrated apps → Upload custom apps."
          href="https://re-stack.com/manifest.xml"
          external
        />
        <ResourceCard
          icon={VerifiedUserRoundedIcon}
          title="Security brief for IT"
          desc="One-page InfoSec brief covering architecture, data privacy, auth, and deployment."
          href="/Restack-Security-Brief.pdf"
          external
        />
        <ResourceCard
          icon={HelpOutlineIcon}
          title="Support"
          desc="Questions, issues, or feedback — we respond fast."
          href="/support"
        />
      </Stack>
    </Box>
  );
}
